<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import AudienceListCard from 'dashboard/components-next/Campaigns/Pages/AudiencePage/AudienceListCard.vue';
import CandidateBaseCard from 'dashboard/components-next/Campaigns/Pages/AudiencePage/CandidateBaseCard.vue';
import AudienceListDialog from 'dashboard/components-next/Campaigns/Pages/AudiencePage/AudienceListDialog.vue';
import ImportAudienceDialog from 'dashboard/components-next/Campaigns/Pages/AudiencePage/ImportAudienceDialog.vue';
import ImportFromRegistryDialog from 'dashboard/components-next/Campaigns/Pages/AudiencePage/ImportFromRegistryDialog.vue';
import AudienceMembersDialog from 'dashboard/components-next/Campaigns/Pages/AudiencePage/AudienceMembersDialog.vue';
import LinkCandidatesDialog from 'dashboard/components-next/Campaigns/Pages/AudiencePage/LinkCandidatesDialog.vue';
import NewCampaignFromAudienceDialog from 'dashboard/components-next/Campaigns/Pages/AudiencePage/NewCampaignFromAudienceDialog.vue';

const { t } = useI18n();
const store = useStore();

const audienceLists = useMapGetter('audienceLists/getAudienceLists');
const uiFlags = useMapGetter('audienceLists/getUIFlags');

const selectedList = ref(null);
const editingList = ref(null);

const audienceListDialogRef = ref(null);
const importDialogRef = ref(null);
const registryDialogRef = ref(null);
const membersDialogRef = ref(null);
const linkCandidatesDialogRef = ref(null);
const newCampaignDialogRef = ref(null);
const deleteDialogRef = ref(null);
const candidateBaseSummary = ref(null);
const isLoadingCandidateBase = ref(false);

const isFetching = computed(() => uiFlags.value.isFetching);
const hasError = computed(() => uiFlags.value.hasError);
// A failed fetch must not fall through to the empty state: "no audiences yet"
// and "we could not load your audiences" call for opposite actions.
const hasNoLists = computed(
  () => audienceLists.value.length === 0 && !isFetching.value && !hasError.value
);

const fetchAudienceLists = () => store.dispatch('audienceLists/get');

const fetchCandidateBaseSummary = async () => {
  isLoadingCandidateBase.value = true;
  try {
    const payload = await store.dispatch('audienceLists/candidatePreview', {
      filters: {},
      selectionLimit: 250,
    });
    candidateBaseSummary.value = payload.summary;
  } catch (error) {
    candidateBaseSummary.value = null;
  } finally {
    isLoadingCandidateBase.value = false;
  }
};

onMounted(() => {
  fetchAudienceLists();
  fetchCandidateBaseSummary();
  // Campaign creation from this page needs labels and inboxes to be loaded;
  // the operator may land here directly from the sidebar.
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
});

const openCreateDialog = () => {
  editingList.value = null;
  audienceListDialogRef.value?.dialogRef?.open();
};

const handleEdit = audienceList => {
  editingList.value = audienceList;
  audienceListDialogRef.value?.dialogRef?.open();
};

const handleImport = audienceList => {
  selectedList.value = audienceList;
  importDialogRef.value?.open();
};

const handleImportRegistry = audienceList => {
  selectedList.value = audienceList;
  registryDialogRef.value?.open();
};

const handleLinkCandidates = audienceList => {
  selectedList.value = audienceList;
  linkCandidatesDialogRef.value?.open();
};

const handleViewMembers = audienceList => {
  selectedList.value = audienceList;
  membersDialogRef.value?.open();
};

const handleCreateCampaign = audienceList => {
  selectedList.value = audienceList;
  newCampaignDialogRef.value?.open();
};

const handleDelete = audienceList => {
  selectedList.value = audienceList;
  deleteDialogRef.value?.open();
};

const confirmDelete = async () => {
  try {
    await store.dispatch('audienceLists/delete', selectedList.value.id);
    useAlert(t('CAMPAIGN.AUDIENCE.API.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CAMPAIGN.AUDIENCE.API.ERROR_MESSAGE')
    );
  } finally {
    deleteDialogRef.value?.close();
  }
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.AUDIENCE.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.AUDIENCE.NEW_LIST')"
    @click="openCreateDialog"
  >
    <div
      v-if="isFetching"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <div
      v-else-if="hasError"
      class="flex flex-col items-center justify-center gap-3 py-10 text-center"
    >
      <p class="max-w-md text-sm text-n-slate-11">
        {{ t('CAMPAIGN.AUDIENCE.ERROR_STATE.TITLE') }}
      </p>
      <Button
        variant="outline"
        icon="i-lucide-refresh-cw"
        :label="t('CAMPAIGN.AUDIENCE.ERROR_STATE.RETRY')"
        @click="fetchAudienceLists"
      />
    </div>

    <div v-else class="flex flex-col gap-4">
      <CandidateBaseCard
        :summary="candidateBaseSummary"
        :is-loading="isLoadingCandidateBase"
        @link-candidates="handleLinkCandidates(null)"
      />

      <div v-if="!hasNoLists" class="flex flex-col gap-4">
        <AudienceListCard
          v-for="audienceList in audienceLists"
          :key="audienceList.id"
          :audience-list="audienceList"
          @import="handleImport"
          @import-registry="handleImportRegistry"
          @link-candidates="handleLinkCandidates"
          @view-members="handleViewMembers"
          @create-campaign="handleCreateCampaign"
          @edit="handleEdit"
          @delete="handleDelete"
        />
      </div>

      <EmptyStateLayout
        v-else
        :title="t('CAMPAIGN.AUDIENCE.EMPTY_STATE.TITLE')"
        :subtitle="t('CAMPAIGN.AUDIENCE.EMPTY_STATE.SUBTITLE')"
        :show-backdrop="false"
        class="pt-10"
      >
        <template #actions>
          <Button
            icon="i-lucide-plus"
            :label="t('CAMPAIGN.AUDIENCE.NEW_LIST')"
            @click="openCreateDialog"
          />
        </template>
      </EmptyStateLayout>
    </div>

    <AudienceListDialog
      ref="audienceListDialogRef"
      :audience-list="editingList"
    />
    <ImportAudienceDialog ref="importDialogRef" :audience-list="selectedList" />
    <ImportFromRegistryDialog
      ref="registryDialogRef"
      :audience-list="selectedList"
    />
    <AudienceMembersDialog
      ref="membersDialogRef"
      :audience-list="selectedList"
    />
    <LinkCandidatesDialog
      ref="linkCandidatesDialogRef"
      :audience-list="selectedList"
      :audience-lists="audienceLists"
    />
    <NewCampaignFromAudienceDialog
      ref="newCampaignDialogRef"
      :audience-list="selectedList"
    />
    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('CAMPAIGN.AUDIENCE.CONFIRM_DELETE.TITLE')"
      :description="t('CAMPAIGN.AUDIENCE.CONFIRM_DELETE.DESCRIPTION')"
      :confirm-button-label="t('CAMPAIGN.AUDIENCE.CONFIRM_DELETE.CONFIRM')"
      @confirm="confirmDelete"
    />
  </CampaignLayout>
</template>
